import { EMPTY_CELL } from 'utils/empty-cell';
import type {
  ConfidenceFactor,
  EntityCard,
  EntityType,
  StructuredPartyDetail,
} from '../types';
import {
  FIRST_SUGGESTION_LABEL,
  anchorCard,
  anchorSourceLabel,
  cardAddresses,
  suggestionLabels,
} from '../utils';
import type { ReconcileCandidate } from './types';

interface CandidateOptions {
  isStaged: boolean;
  sourceLabel: string;
  confidence?: number | null;
  recommended?: boolean;
  confidenceFactors?: ConfidenceFactor[];
}

type AddressFlagCriticality = 'high' | 'medium' | null;

/**
 * The criticality of an address's review flag, defaulting to medium.
 * @param {EntityCard['primaryAddress']} address - The address to read.
 * @return {AddressFlagCriticality} The flag's criticality, or null when unflagged.
 */
const flagCriticality = (
  address: EntityCard['primaryAddress']
): AddressFlagCriticality => {
  if (!address?.reviewFlag) return null;
  return address.reviewFlag.criticality ?? 'medium';
};

/**
 * An entity has an unresolved address flag when any of its addresses carries a
 * reviewFlag (the agent flagged the address; it must be resolved on Match Review
 * before the entity is eligible to reconcile).
 * @param {EntityCard} card - The entity.
 * @return {boolean} Whether any address is flagged.
 */
function hasUnresolvedAddressFlag(card: EntityCard): boolean {
  return cardAddresses(card).some((address) => Boolean(address.reviewFlag));
}

/**
 * A reconcile row for an entity card.
 * @param {EntityCard} card - The entity.
 * @param {CandidateOptions} options - The row's staging, label and scoring.
 * @return {ReconcileCandidate} The row.
 */
function candidateFromEntityCard(
  card: EntityCard,
  {
    isStaged,
    sourceLabel,
    confidence = null,
    recommended = false,
    confidenceFactors,
  }: CandidateOptions
): ReconcileCandidate {
  const addresses = cardAddresses(card);
  return {
    id: card.id,
    name: card.name ?? EMPTY_CELL,
    type: card.type,
    address: card.primaryAddress?.fullText ?? addresses[0]?.fullText ?? null,
    isStaged,
    sourceLabel,
    confidence,
    phone: card.phoneNumber ?? null,
    recommended,
    flagged: hasUnresolvedAddressFlag(card),
    flagCategory: card.flagCategory ?? null,
    flagRationale: card.flagRationale ?? null,
    flagSeverity: card.flagSeverity ?? null,
    confidenceFactors,
    addresses: addresses.map((address) => ({
      id: address.id,
      label: address.fullText,
    })),
    titleWorkspaces: card.titleWorkspaces,
    interpretationCount: card.interpretationCount,
  };
}

/**
 * A candidate's addresses, each marked by its own review flag, so the tearsheet
 * highlights and badges every flagged address of an anchor row.
 * @param {Array} addresses - Each address's id, label and flag criticality (null when unflagged).
 * @return {ReconcileCandidate['addresses']} The address rows.
 */
export const flaggedAddressRows = (
  addresses: {
    id: number;
    label: string;
    criticality: AddressFlagCriticality;
  }[]
): ReconcileCandidate['addresses'] =>
  addresses.map(({ id, label, criticality }) => ({
    id,
    label,
    flagged: criticality !== null,
    criticality,
  }));

/**
 * The anchor row also carries its address-confidence flags, per address, so the
 * tearsheet marks it (and gates submission) like the entity page's viewed entity.
 * @param {EntityCard} card - The anchor entity.
 * @param {string} sourceLabel - The label the anchor tile shows.
 * @param {number | null} confidence - The arrival confidence.
 * @return {ReconcileCandidate} The anchor row.
 */
function anchorCandidate(
  card: EntityCard,
  sourceLabel: string,
  confidence: number | null
): ReconcileCandidate {
  const addresses = cardAddresses(card);
  const criticalities = addresses.map(flagCriticality);
  const flaggedCriticality = criticalities.includes('high')
    ? 'high'
    : (criticalities.find(Boolean) ?? null);
  return {
    ...candidateFromEntityCard(card, {
      isStaged: true,
      sourceLabel,
      confidence,
    }),
    addressFlagged: flaggedCriticality !== null,
    addressFlagCriticality: flaggedCriticality,
    addresses: flaggedAddressRows(
      addresses.map((address, index) => ({
        id: address.id,
        label: address.fullText,
        criticality: criticalities[index],
      }))
    ),
  };
}

/**
 * Build the reconcile candidate set from a party detail: the party's current
 * anchor first (the staged entity, or the Deduplicated Result it was matched
 * into), then each distinct suggestion under the label the tiles show. A
 * collapsed alias family shows its display member. Deduped by entity id (first
 * wins).
 * @param {StructuredPartyDetail} detail - The party detail.
 * @return {ReconcileCandidate[]} The candidate rows.
 */
export function buildReconcileCandidates(
  detail: StructuredPartyDetail
): ReconcileCandidate[] {
  const byId = new Map<number, ReconcileCandidate>();

  const anchor = anchorCard(detail);
  if (anchor) {
    byId.set(
      anchor.id,
      anchorCandidate(
        anchor,
        anchorSourceLabel(detail.currentAnchor?.source),
        detail.currentAnchor ? detail.arrivalConfidence : null
      )
    );
  }

  const labels = suggestionLabels(detail.suggestions);
  detail.suggestions.forEach((suggestion) => {
    const entity = suggestion.displayEntity ?? suggestion.suggestedEntity;
    const sourceLabel = labels.get(suggestion.id);
    if (!entity || !sourceLabel || byId.has(entity.id)) return;
    byId.set(
      entity.id,
      candidateFromEntityCard(entity, {
        isStaged: false,
        sourceLabel,
        confidence: suggestion.confidenceScore,
        recommended:
          sourceLabel === FIRST_SUGGESTION_LABEL &&
          suggestion.isAiRecommendation,
        confidenceFactors: suggestion.confidenceFactors,
      })
    );
  });

  return Array.from(byId.values());
}

/**
 * Mirrors Reconcile::Orchestrator#surviving_entity: prefer a canonical (non-
 * Imported) member, else the whole group; then the lowest id. The tearsheet
 * seeds its survivor overrides with this so the UI matches the backend default.
 * @param {number[]} entityIds - The group's entity ids.
 * @param {Record<number, { type: EntityType }>} candidatesById - The candidates'
 *   types by id; any candidate-shaped record works.
 * @return {number} The survivor's id.
 */
export function defaultSurvivorId(
  entityIds: number[],
  candidatesById: Record<number, { type: EntityType }>
): number {
  const canonical = entityIds.filter(
    (id) => candidatesById[id] && candidatesById[id].type !== 'ImportedEntity'
  );
  const pool = canonical.length > 0 ? canonical : entityIds;
  return Math.min(...pool);
}
